import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart3, ArrowLeft, Search, Download, RotateCcw, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthReady } from '@/lib/ensureAuthReady';
import { toast } from 'sonner';
import { companyService } from '@/services/companyService';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { triggerReloadWithTrafficAnalysisFilters, pickReloadSearch } from '@/utils/reloadSearch';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import * as XLSX from 'xlsx';

// 統計數據接口
interface DateStat {
  date: string;
  orderCount: number;
}

interface TimeStat {
  timeSlot: string;
  orderCount: number;
}

interface QuantityStat {
  quantity: number;
  count: number;
}

interface DistrictStat {
  district: string;
  orderCount: number;
}

interface SearchFilters {
  startDate: string;
  endDate: string;
  skuId: string;
  brand: string;
  productName: string;
}

// 時間桶定義
const TIME_SLOTS = [
  { slot: '00:00-08:59', start: '00:00:00', end: '08:59:59', label_zh: '凌晨 (00:00-08:59)', label_en: 'Early Morning (00:00-08:59)' },
  { slot: '09:00-11:59', start: '09:00:00', end: '11:59:59', label_zh: '上午 (09:00-11:59)', label_en: 'Morning (09:00-11:59)' },
  { slot: '12:00-14:59', start: '12:00:00', end: '14:59:59', label_zh: '午後 (12:00-14:59)', label_en: 'Afternoon (12:00-14:59)' },
  { slot: '15:00-17:59', start: '15:00:00', end: '17:59:59', label_zh: '下午 (15:00-17:59)', label_en: 'Late Afternoon (15:00-17:59)' },
  { slot: '18:00-20:59', start: '18:00:00', end: '20:59:59', label_zh: '傍晚 (18:00-20:59)', label_en: 'Evening (18:00-20:59)' },
  { slot: '21:00-23:59', start: '21:00:00', end: '23:59:59', label_zh: '夜晚 (21:00-23:59)', label_en: 'Night (21:00-23:59)' }
];

// 圖表顏色配置
const CHART_COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#6366f1', '#14b8a6'];

const ANALYTICS_CATEGORY: CategoryKey = 'analytics';

const TrafficAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);
  const [loading, setLoading] = useState(false);

  // 統計數據狀態
  const [dateStats, setDateStats] = useState<DateStat[]>([]);
  const [timeStats, setTimeStats] = useState<TimeStat[]>([]);
  const [quantityStats, setQuantityStats] = useState<QuantityStat[]>([]);
  const [skuCountStats, setSkuCountStats] = useState<QuantityStat[]>([]);
  const [districtStats, setDistrictStats] = useState<DistrictStat[]>([]);

  // 獲取昨天的日期
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // 獲取當月第1日
  const getFirstDayOfMonth = () => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  };

  const [filters, setFilters] = useState<SearchFilters>({
    startDate: getFirstDayOfMonth(),
    endDate: getYesterday(),
    skuId: '',
    brand: '',
    productName: ''
  });

  // 初始化公司資訊
  useEffect(() => {
    const initializeCompany = async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) {
          setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
        }
      } catch (error) {
        console.error('Failed to get company:', error);
        toast.error(language === 'zh' ? '獲取公司資訊失敗' : 'Failed to get company information');
      }
    };

    initializeCompany();
  }, [language]);

  // 首次載入時讀取回放條件並保存
  useEffect(() => {
    const pending = pickReloadSearch();
    if (!pending) return;

    pendingReloadRef.current = pending;

    // 立即保存待回放的條件到 UI 狀態，避免遺失
    console.log('🔄 保存待回放搜尋條件到 UI 狀態');
    setFilters(pending.filters);
  }, []);

  // 公司資訊就緒後回放搜尋
  useEffect(() => {
    if (!currentCompany?.id) {
      console.log('🔄 等待公司資訊就緒...');
      return;
    }

    const pending = pendingReloadRef.current;
    if (!pending) return;

    const executePendingSearch = async () => {
      console.log('🔄 檢測到待回放的搜索，開始執行');
      try {
        // 保險：確保 Auth ready 再搜
        await ensureAuthReady();
        runAnalysis(pending.filters);
        toast.success(language === 'zh' ? '搜索條件已恢復並執行' : 'Search conditions restored and executed');
      } catch (error) {
        console.error('回放搜索失敗:', error);
        toast.error(language === 'zh' ? '搜索恢復失敗，請重新搜尋' : 'Search restore failed, please search again');
      } finally {
        pendingReloadRef.current = null;
      }
    };

    executePendingSearch();
  }, [currentCompany, language]); // 移除 runAnalysis 依賴避免無限循環

  const ctrlRef = useRef<AbortController | null>(null);

  // 暫存待回放搜尋條件，確保公司資訊就緒後仍能執行
  const pendingReloadRef = useRef<ReturnType<typeof pickReloadSearch>>(null);

  // 分批取數輔助函式（避免 Supabase 1000 筆限制）
  const fetchAllData = async <T,>(
    query: any,
    signal: AbortSignal,
    batchSize: number = 1000
  ): Promise<T[]> => {
    let allData: T[] = [];
    let start = 0;
    let hasMore = true;

    while (hasMore) {
      // 檢查是否已中止
      if (signal.aborted) {
        throw new Error('AbortError');
      }

      // 執行分批查詢
      const { data, error, count } = await query
        .range(start, start + batchSize - 1)
        .abortSignal(signal);

      if (error) throw error;

      if (data && data.length > 0) {
        allData = [...allData, ...data];
        start += batchSize;
        
        // 如果返回的數據少於 batchSize，表示已經是最後一批
        hasMore = data.length === batchSize;
      } else {
        hasMore = false;
      }
    }

    return allData;
  };

  // 查詢購買日子統計
  const fetchDateStats = async (signal: AbortSignal, searchFilters: SearchFilters, companyId: number) => {
    let query = supabase
      .from('sales_orders')
      .select('order_id, order_date, order_datetime, sku_id, company_sku_key, sale_sku_brand_en, sale_sku_brand_zh, sale_sku_name_en, sale_sku_name_zh')
      .eq('company_id', companyId);

    // 應用時間範圍和其他篩選
    if (searchFilters.startDate) {
      query = query.gte('order_date', searchFilters.startDate);
    }
    if (searchFilters.endDate) {
      query = query.lte('order_date', searchFilters.endDate);
    }
    if (searchFilters.skuId) {
      query = query.or(`sku_id.ilike.%${searchFilters.skuId}%,company_sku_key.ilike.%${searchFilters.skuId}%`);
    }
    if (searchFilters.brand) {
      query = query.or(`sale_sku_brand_en.ilike.%${searchFilters.brand}%,sale_sku_brand_zh.ilike.%${searchFilters.brand}%`);
    }
    if (searchFilters.productName) {
      query = query.or(`sale_sku_name_en.ilike.%${searchFilters.productName}%,sale_sku_name_zh.ilike.%${searchFilters.productName}%`);
    }

    // 使用分批取數輔助函式獲取所有數據
    const data = await fetchAllData<any>(query, signal);

    // 按日期分組計算distinct order_id
    const dateMap = new Map<string, Set<string>>();
    data?.forEach(item => {
      const date = item.order_date || item.order_datetime?.split('T')[0] || '';
      if (date) {
        if (!dateMap.has(date)) {
          dateMap.set(date, new Set());
        }
        dateMap.get(date)?.add(item.order_id);
      }
    });

    const result: DateStat[] = Array.from(dateMap.entries())
      .map(([date, orderIds]) => ({
        date,
        orderCount: orderIds.size
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return result;
  };

  // 查詢購買時間區段統計（使用sales_orders）
  const fetchTimeStats = async (signal: AbortSignal, searchFilters: SearchFilters, companyId: number) => {
    let query = supabase
      .from('sales_orders')
      .select('order_id, order_time, sku_id, company_sku_key, sale_sku_brand_en, sale_sku_brand_zh, sale_sku_name_en, sale_sku_name_zh, order_date')
      .eq('company_id', companyId);

    // 應用時間範圍和其他篩選
    if (searchFilters.startDate) {
      query = query.gte('order_date', searchFilters.startDate);
    }
    if (searchFilters.endDate) {
      query = query.lte('order_date', searchFilters.endDate);
    }
    if (searchFilters.skuId) {
      query = query.or(`sku_id.ilike.%${searchFilters.skuId}%,company_sku_key.ilike.%${searchFilters.skuId}%`);
    }
    if (searchFilters.brand) {
      query = query.or(`sale_sku_brand_en.ilike.%${searchFilters.brand}%,sale_sku_brand_zh.ilike.%${searchFilters.brand}%`);
    }
    if (searchFilters.productName) {
      query = query.or(`sale_sku_name_en.ilike.%${searchFilters.productName}%,sale_sku_name_zh.ilike.%${searchFilters.productName}%`);
    }

    // 使用分批取數輔助函式獲取所有數據
    const data = await fetchAllData<any>(query, signal);

    // 按時間桶分組計算distinct order_id
    const timeMap = new Map<string, Set<string>>();
    TIME_SLOTS.forEach(slot => {
      timeMap.set(slot.slot, new Set());
    });

    data?.forEach(item => {
      if (item.order_time) {
        const time = item.order_time;
        const slot = TIME_SLOTS.find(s => time >= s.start && time <= s.end);
        if (slot) {
          timeMap.get(slot.slot)?.add(item.order_id);
        }
      }
    });

    const result: TimeStat[] = TIME_SLOTS.map(slot => ({
      timeSlot: language === 'zh' ? slot.label_zh : slot.label_en,
      orderCount: timeMap.get(slot.slot)?.size || 0
    }));

    return result;
  };

  // 查詢單筆多件購買比例（sale_quantity分布）
  const fetchQuantityStats = async (signal: AbortSignal, searchFilters: SearchFilters, companyId: number) => {
    let query = supabase
      .from('sales_orders')
      .select('order_id, sku_id, company_sku_key, sale_quantity, sale_sku_brand_en, sale_sku_brand_zh, sale_sku_name_en, sale_sku_name_zh, order_date')
      .eq('company_id', companyId);

    // 應用時間範圍和其他篩選
    if (searchFilters.startDate) {
      query = query.gte('order_date', searchFilters.startDate);
    }
    if (searchFilters.endDate) {
      query = query.lte('order_date', searchFilters.endDate);
    }
    if (searchFilters.skuId) {
      query = query.or(`sku_id.ilike.%${searchFilters.skuId}%,company_sku_key.ilike.%${searchFilters.skuId}%`);
    }
    if (searchFilters.brand) {
      query = query.or(`sale_sku_brand_en.ilike.%${searchFilters.brand}%,sale_sku_brand_zh.ilike.%${searchFilters.brand}%`);
    }
    if (searchFilters.productName) {
      query = query.or(`sale_sku_name_en.ilike.%${searchFilters.productName}%,sale_sku_name_zh.ilike.%${searchFilters.productName}%`);
    }

    // 使用分批取數輔助函式獲取所有數據
    const data = await fetchAllData<any>(query, signal);

    // 按order_id分組，計算每個訂單的總數量
    const orderQuantityMap = new Map<string, number>();
    data?.forEach(item => {
      const orderId = item.order_id;
      const qty = item.sale_quantity || 1;
      orderQuantityMap.set(orderId, (orderQuantityMap.get(orderId) || 0) + qty);
    });

    // 統計每個總數量的訂單數
    const quantityMap = new Map<number, number>();
    orderQuantityMap.forEach((totalQty) => {
      quantityMap.set(totalQty, (quantityMap.get(totalQty) || 0) + 1);
    });

    const result: QuantityStat[] = Array.from(quantityMap.entries())
      .map(([quantity, count]) => ({ quantity, count }))
      .sort((a, b) => a.quantity - b.quantity);

    return result;
  };

  // 查詢每單購買不同產品數量比例
  const fetchSkuCountStats = async (signal: AbortSignal, searchFilters: SearchFilters, companyId: number) => {
    let query = supabase
      .from('sales_orders')
      .select('order_id, sku_id, company_sku_key, sale_sku_brand_en, sale_sku_brand_zh, sale_sku_name_en, sale_sku_name_zh, order_date')
      .eq('company_id', companyId);

    // 應用時間範圍和其他篩選
    if (searchFilters.startDate) {
      query = query.gte('order_date', searchFilters.startDate);
    }
    if (searchFilters.endDate) {
      query = query.lte('order_date', searchFilters.endDate);
    }
    if (searchFilters.skuId) {
      query = query.or(`sku_id.ilike.%${searchFilters.skuId}%,company_sku_key.ilike.%${searchFilters.skuId}%`);
    }
    if (searchFilters.brand) {
      query = query.or(`sale_sku_brand_en.ilike.%${searchFilters.brand}%,sale_sku_brand_zh.ilike.%${searchFilters.brand}%`);
    }
    if (searchFilters.productName) {
      query = query.or(`sale_sku_name_en.ilike.%${searchFilters.productName}%,sale_sku_name_zh.ilike.%${searchFilters.productName}%`);
    }

    // 使用分批取數輔助函式獲取所有數據
    const data = await fetchAllData<any>(query, signal);

    // 按order_id分組，計算每個訂單的distinct sku數量
    const orderSkuMap = new Map<string, Set<string>>();
    data?.forEach(item => {
      if (!orderSkuMap.has(item.order_id)) {
        orderSkuMap.set(item.order_id, new Set());
      }
      orderSkuMap.get(item.order_id)?.add(item.company_sku_key || item.sku_id);
    });

    // 統計每個數量的訂單數
    const skuCountMap = new Map<number, number>();
    orderSkuMap.forEach((skuSet) => {
      const count = skuSet.size;
      skuCountMap.set(count, (skuCountMap.get(count) || 0) + 1);
    });

    const result: QuantityStat[] = Array.from(skuCountMap.entries())
      .map(([quantity, count]) => ({ quantity, count }))
      .sort((a, b) => a.quantity - b.quantity);

    return result;
  };

  // 查詢銷售地區統計
  const fetchDistrictStats = async (signal: AbortSignal, searchFilters: SearchFilters, companyId: number) => {
    let query = supabase
      .from('sales_orders')
      .select('order_id, delivery_district, sku_id, company_sku_key, sale_sku_brand_en, sale_sku_brand_zh, sale_sku_name_en, sale_sku_name_zh, order_date')
      .eq('company_id', companyId);

    // 應用時間範圍和其他篩選
    if (searchFilters.startDate) {
      query = query.gte('order_date', searchFilters.startDate);
    }
    if (searchFilters.endDate) {
      query = query.lte('order_date', searchFilters.endDate);
    }
    if (searchFilters.skuId) {
      query = query.or(`sku_id.ilike.%${searchFilters.skuId}%,company_sku_key.ilike.%${searchFilters.skuId}%`);
    }
    if (searchFilters.brand) {
      query = query.or(`sale_sku_brand_en.ilike.%${searchFilters.brand}%,sale_sku_brand_zh.ilike.%${searchFilters.brand}%`);
    }
    if (searchFilters.productName) {
      query = query.or(`sale_sku_name_en.ilike.%${searchFilters.productName}%,sale_sku_name_zh.ilike.%${searchFilters.productName}%`);
    }

    // 使用分批取數輔助函式獲取所有數據
    const data = await fetchAllData<any>(query, signal);

    // 按地區分組計算distinct order_id
    const districtMap = new Map<string, Set<string>>();
    data?.forEach(item => {
      const district = item.delivery_district || (language === 'zh' ? '未指定' : 'Unspecified');
      if (!districtMap.has(district)) {
        districtMap.set(district, new Set());
      }
      districtMap.get(district)?.add(item.order_id);
    });

    const result: DistrictStat[] = Array.from(districtMap.entries())
      .map(([district, orderIds]) => ({
        district,
        orderCount: orderIds.size
      }))
      .sort((a, b) => b.orderCount - a.orderCount)
      .slice(0, 15); // 只顯示前15個地區

    return result;
  };

  // 執行統計查詢
  const runAnalysis = useCallback(async (searchFilters: SearchFilters) => {
    if (!currentCompany?.id) {
      toast.error(language === 'zh' ? '缺少公司資訊，請重新載入頁面' : 'Missing company information, please reload the page');
      return;
    }

    try {
      await ensureAuthReady();

      ctrlRef.current?.abort();
      const controller = new AbortController();
      ctrlRef.current = controller;
      const { signal } = controller;

      setLoading(true);

      const timeoutMs = 60000; // 60秒超時
      const timer = setTimeout(() => {
        controller.abort();
      }, timeoutMs);

      try {
        // 並行執行所有查詢
        const [dates, times, quantities, skuCounts, districts] = await Promise.all([
          fetchDateStats(signal, searchFilters, Number(currentCompany.id)),
          fetchTimeStats(signal, searchFilters, Number(currentCompany.id)),
          fetchQuantityStats(signal, searchFilters, Number(currentCompany.id)),
          fetchSkuCountStats(signal, searchFilters, Number(currentCompany.id)),
          fetchDistrictStats(signal, searchFilters, Number(currentCompany.id))
        ]);

        setDateStats(dates);
        setTimeStats(times);
        setQuantityStats(quantities);
        setSkuCountStats(skuCounts);
        setDistrictStats(districts);

        toast.success(language === 'zh' ? '流量分析完成' : 'Traffic analysis completed');
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          console.error('分析錯誤:', e);
          toast.error(language === 'zh' ? '分析過程中發生錯誤' : 'Error occurred during analysis');
        }
      } finally {
        clearTimeout(timer);
        setLoading(false);
        if (ctrlRef.current === controller) ctrlRef.current = null;
      }
    } catch (e: any) {
      console.error('執行錯誤:', e);
      if (e.name !== 'AbortError') {
        toast.error(language === 'zh' ? '執行過程中發生錯誤' : 'Error occurred during execution');
      }
    }
  }, [language, currentCompany]);

  // 搜尋按鈕處理
  const handleSearch = () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error(language === 'zh' ? '請選擇開始和結束日期' : 'Please select start and end dates');
      return;
    }

    // 檢查日期範圍
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    const daysDiff = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

    if (daysDiff > 365) {
      const proceed = window.confirm(
        language === 'zh'
          ? '日期範圍超過1年，查詢可能較慢。是否繼續？'
          : 'Date range exceeds 1 year, query may be slow. Continue?'
      );
      if (!proceed) return;
    }

    // 🔄 使用專業的刷新搜索機制
    console.log('🔄 觸發專業刷新搜索，條件:', filters);
    triggerReloadWithTrafficAnalysisFilters(filters);
  };

  // 清除篩選條件
  const clearFilters = () => {
    setFilters({
      startDate: getFirstDayOfMonth(),
      endDate: getYesterday(),
      skuId: '',
      brand: '',
      productName: ''
    });
    // 清除統計結果
    setDateStats([]);
    setTimeStats([]);
    setQuantityStats([]);
    setSkuCountStats([]);
    setDistrictStats([]);
    toast.info(language === 'zh' ? '篩選條件已清除，日期重設為當月第1日' : 'Filters cleared, dates reset to first day of month');
  };

  // 匯出功能
  const exportData = () => {
    if (dateStats.length === 0 && timeStats.length === 0 && quantityStats.length === 0 && 
        skuCountStats.length === 0 && districtStats.length === 0) {
      toast.error(language === 'zh' ? '沒有資料可匯出' : 'No data to export');
      return;
    }

    try {
      const wb = XLSX.utils.book_new();

      // 購買日子工作表
      if (dateStats.length > 0) {
        const ws1 = XLSX.utils.json_to_sheet(
          dateStats.map(d => ({
            [language === 'zh' ? '日期' : 'Date']: d.date,
            [language === 'zh' ? '訂單數' : 'Order Count']: d.orderCount
          }))
        );
        XLSX.utils.book_append_sheet(wb, ws1, language === 'zh' ? '購買日子' : 'Purchase Dates');
      }

      // 購買時間工作表
      if (timeStats.length > 0) {
        const ws2 = XLSX.utils.json_to_sheet(
          timeStats.map(t => ({
            [language === 'zh' ? '時間區段' : 'Time Slot']: t.timeSlot,
            [language === 'zh' ? '訂單數' : 'Order Count']: t.orderCount
          }))
        );
        XLSX.utils.book_append_sheet(wb, ws2, language === 'zh' ? '購買時間' : 'Purchase Time');
      }

      // 單筆數量工作表
      if (quantityStats.length > 0) {
        const ws3 = XLSX.utils.json_to_sheet(
          quantityStats.map(q => ({
            [language === 'zh' ? '數量' : 'Quantity']: q.quantity,
            [language === 'zh' ? '筆數' : 'Count']: q.count
          }))
        );
        XLSX.utils.book_append_sheet(wb, ws3, language === 'zh' ? '單筆數量' : 'Item Quantity');
      }

      // 不同產品數工作表
      if (skuCountStats.length > 0) {
        const ws4 = XLSX.utils.json_to_sheet(
          skuCountStats.map(s => ({
            [language === 'zh' ? '產品數' : 'Product Count']: s.quantity,
            [language === 'zh' ? '訂單數' : 'Order Count']: s.count
          }))
        );
        XLSX.utils.book_append_sheet(wb, ws4, language === 'zh' ? '不同產品數' : 'Unique Products');
      }

      // 銷售地區工作表
      if (districtStats.length > 0) {
        const ws5 = XLSX.utils.json_to_sheet(
          districtStats.map(d => ({
            [language === 'zh' ? '地區' : 'District']: d.district,
            [language === 'zh' ? '訂單數' : 'Order Count']: d.orderCount
          }))
        );
        XLSX.utils.book_append_sheet(wb, ws5, language === 'zh' ? '銷售地區' : 'Sales District');
      }

      XLSX.writeFile(wb, `traffic_analysis_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(language === 'zh' ? '匯出成功' : 'Export successful');
    } catch (error) {
      console.error('匯出錯誤:', error);
      toast.error(language === 'zh' ? '匯出失敗' : 'Export failed');
    }
  };

  // 組件卸載時清理
  useEffect(() => {
    return () => {
      ctrlRef.current?.abort();
    };
  }, []);

  const hasData = dateStats.length > 0 || timeStats.length > 0 || quantityStats.length > 0 || 
                  skuCountStats.length > 0 || districtStats.length > 0;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => {
                localStorage.setItem('selectedCategory', ANALYTICS_CATEGORY);
                navigate(ROUTES.dashboard.root);
              }}
              className="bg-slate-800/50 border-slate-600 text-slate-300 hover:bg-slate-700/50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
            </Button>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-purple-400" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{language === 'zh' ? '流量分析' : 'Traffic Analysis'}</h1>
            </div>
          </div>
          
          {/* 頁面導覽按鈕 */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/orders/statistics')}
              className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
              title={language === 'zh' ? '銷售統計' : 'Sales Statistics'}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <BarChart3 className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-600/20 border border-purple-500/30 rounded-md">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              <span className="text-purple-300 text-sm font-medium">
                {language === 'zh' ? '流量分析' : 'Traffic Analysis'}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/analytics/slow-moving')}
              className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
              title={language === 'zh' ? '慢流產品分析' : 'Slow-moving Products'}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* 搜尋條件區域 */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Search className="w-5 h-5" />
              {language === 'zh' ? '搜尋條件' : 'Search Criteria'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
              <div>
                <Label htmlFor="startDate" className="text-muted-foreground">
                  {language === 'zh' ? '開始日期 *' : 'Start Date *'}
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  required
                />
              </div>
              <div>
                <Label htmlFor="endDate" className="text-muted-foreground">
                  {language === 'zh' ? '結束日期 *' : 'End Date *'}
                </Label>
                <Input
                  id="endDate"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  required
                />
              </div>
              <div>
                <Label htmlFor="skuId" className="text-muted-foreground">SKU ID</Label>
                <Input
                  id="skuId"
                  value={filters.skuId}
                  onChange={(e) => setFilters(prev => ({ ...prev, skuId: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  placeholder={language === 'zh' ? '輸入產品編號' : 'Enter SKU ID'}
                />
              </div>
              <div>
                <Label htmlFor="brand" className="text-muted-foreground">{language === 'zh' ? '品牌' : 'Brand'}</Label>
                <Input
                  id="brand"
                  value={filters.brand}
                  onChange={(e) => setFilters(prev => ({ ...prev, brand: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  placeholder={language === 'zh' ? '輸入品牌名' : 'Enter brand name'}
                />
              </div>
              <div>
                <Label htmlFor="productName" className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</Label>
                <Input
                  id="productName"
                  value={filters.productName}
                  onChange={(e) => setFilters(prev => ({ ...prev, productName: e.target.value }))}
                  className="bg-background/50 border-input text-foreground"
                  placeholder={language === 'zh' ? '輸入產品名稱' : 'Enter product name'}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSearch}
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
              >
                <Search className="w-4 h-4 mr-2" />
                {loading ? (language === 'zh' ? '分析中...' : 'Analyzing...') : (language === 'zh' ? '開始分析' : 'Start Analysis')}
              </Button>
              <Button
                onClick={clearFilters}
                disabled={loading}
                variant="outline"
                className="bg-card/50 border-border text-foreground hover:bg-secondary/50"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {language === 'zh' ? '清除篩選' : 'Clear Filters'}
              </Button>
              <Button
                onClick={exportData}
                disabled={!hasData}
                variant="outline"
                className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50 disabled:opacity-50"
              >
                <Download className="w-4 h-4 mr-2" />
                {language === 'zh' ? '匯出' : 'Export'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 圖表區域 */}
        {hasData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* 1. 每日銷售單數 - 線性圖（佔整行）*/}
            {dateStats.length > 0 && (
              <Card className="bg-card/80 border-border lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-foreground text-lg">
                    {language === 'zh' ? '每日銷售單數' : 'Daily Sales Orders'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dateStats}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#94a3b8"
                          angle={dateStats.length >= 7 && dateStats.length <= 31 ? -45 : 0}
                          textAnchor={dateStats.length >= 7 && dateStats.length <= 31 ? "end" : "middle"}
                          height={dateStats.length >= 7 && dateStats.length <= 31 ? 80 : 60}
                          tick={dateStats.length <= 31 ? { fontSize: dateStats.length >= 7 ? 10 : 11 } : false}
                          tickFormatter={(value) => {
                            // 7-31天：顯示完整日期（月/日格式）
                            const date = new Date(value);
                            return `${date.getMonth() + 1}/${date.getDate()}`;
                          }}
                          interval={0}
                          label={dateStats.length > 31 ? {
                            value: (() => {
                              const startDate = new Date(dateStats[0].date);
                              const endDate = new Date(dateStats[dateStats.length - 1].date);
                              return `${startDate.getMonth() + 1}-${startDate.getDate()}-${startDate.getFullYear()} - ${endDate.getMonth() + 1}-${endDate.getDate()}-${endDate.getFullYear()}`;
                            })(),
                            position: 'insideBottom',
                            offset: 0,
                            style: { 
                              fill: '#e2e8f0', 
                              fontSize: 14, 
                              fontWeight: 500,
                              textAnchor: 'middle'
                            }
                          } : undefined}
                        />
                        <YAxis stroke="#94a3b8" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                          labelStyle={{ color: '#cbd5e1' }}
                          itemStyle={{ color: '#e2e8f0' }}
                          labelFormatter={(value) => new Date(value).toLocaleDateString(language === 'zh' ? 'zh-TW' : 'en-US')}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="orderCount" 
                          stroke="#8b5cf6" 
                          strokeWidth={2}
                          dot={{ fill: '#8b5cf6', r: dateStats.length <= 31 ? 4 : 2 }}
                          activeDot={{ r: 6 }}
                          name={language === 'zh' ? '訂單數' : 'Orders'}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 2. 銷售時段分析 - 3D柱狀圖 + 數據列表（佔整行）*/}
            {timeStats.length > 0 && (
              <Card className="bg-card/80 border-border lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-foreground text-lg">
                    {language === 'zh' ? '銷售時段分析' : 'Sales Time Period Analysis'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    {/* 左側：真3D效果圖表 */}
                    <div className="flex-1 h-[450px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={timeStats}
                          margin={{ top: 10, right: 30, bottom: 70, left: 10 }}
                        >
                          <defs>
                            {/* 3D漸層效果 - 前面 */}
                            <linearGradient id="colorBar3D" x1="0" y1="0" x2="1" y2="1">
                              <stop offset="0%" stopColor="#f472b6" stopOpacity={1}/>
                              <stop offset="50%" stopColor="#ec4899" stopOpacity={0.95}/>
                              <stop offset="100%" stopColor="#be185d" stopOpacity={0.85}/>
                            </linearGradient>
                            
                            {/* 3D側面效果 */}
                            <linearGradient id="colorBarSide" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor="#be185d" stopOpacity={0.7}/>
                              <stop offset="100%" stopColor="#831843" stopOpacity={0.9}/>
                            </linearGradient>
                            
                            {/* 深度陰影 */}
                            <filter id="shadow3D" x="-50%" y="-50%" width="200%" height="200%">
                              <feGaussianBlur in="SourceAlpha" stdDeviation="4"/>
                              <feOffset dx="3" dy="6" result="offsetblur"/>
                              <feComponentTransfer>
                                <feFuncA type="linear" slope="0.4"/>
                              </feComponentTransfer>
                              <feMerge>
                                <feMergeNode/>
                                <feMergeNode in="SourceGraphic"/>
                              </feMerge>
                            </filter>
                            
                            {/* 頂部光澤 */}
                            <radialGradient id="topGloss" cx="50%" cy="0%" r="50%">
                              <stop offset="0%" stopColor="#fce7f3" stopOpacity={0.4}/>
                              <stop offset="100%" stopColor="#ec4899" stopOpacity={0}/>
                            </radialGradient>
                          </defs>
                          
                          <CartesianGrid 
                            strokeDasharray="3 3" 
                            stroke="#475569" 
                            strokeOpacity={0.2}
                            vertical={false}
                          />
                          
                          <XAxis 
                            dataKey="timeSlot" 
                            stroke="#94a3b8"
                            angle={-40}
                            textAnchor="end"
                            height={90}
                            tick={{ fontSize: 11, fill: '#cbd5e1', fontWeight: 500 }}
                            axisLine={{ stroke: '#64748b', strokeWidth: 2 }}
                            tickLine={{ stroke: '#64748b' }}
                          />
                          
                          <YAxis 
                            stroke="#94a3b8"
                            tick={{ fontSize: 12, fill: '#cbd5e1', fontWeight: 500 }}
                            axisLine={{ stroke: '#64748b', strokeWidth: 2 }}
                            tickLine={{ stroke: '#64748b' }}
                            label={{ 
                              value: language === 'zh' ? '訂單數' : 'Orders', 
                              angle: -90, 
                              position: 'insideLeft',
                              style: { fill: '#cbd5e1', fontSize: 13, fontWeight: 600 }
                            }}
                          />
                          
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#0f172a', 
                              border: '2px solid #ec4899', 
                              borderRadius: '16px',
                              boxShadow: '0 20px 40px rgba(236, 72, 153, 0.3), 0 0 20px rgba(236, 72, 153, 0.2)',
                              padding: '12px 16px'
                            }}
                            labelStyle={{ color: '#f9a8d4', fontWeight: 'bold', fontSize: 14 }}
                            itemStyle={{ color: '#fce7f3', fontSize: 15, fontWeight: 600 }}
                            cursor={{ fill: 'rgba(236, 72, 153, 0.08)' }}
                          />
                          
                          <Bar 
                            dataKey="orderCount" 
                            fill="url(#colorBar3D)"
                            radius={[12, 12, 0, 0]}
                            filter="url(#shadow3D)"
                            name={language === 'zh' ? '訂單數' : 'Orders'}
                            maxBarSize={80}
                          >
                            {timeStats.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`}
                                fill="url(#colorBar3D)"
                                stroke="#fce7f3"
                                strokeWidth={2}
                                style={{
                                  filter: 'drop-shadow(4px 8px 12px rgba(0, 0, 0, 0.4))',
                                }}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* 右側：緊湊數據列表（6行無滾動）*/}
                    <div className="w-56 h-[450px] flex flex-col justify-center space-y-[10px]">
                      {timeStats.map((stat, idx) => (
                        <div 
                          key={idx}
                          className="bg-gradient-to-r from-slate-700/60 to-slate-700/40 backdrop-blur-sm rounded-lg p-2.5 border border-slate-600/40 hover:border-pink-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-pink-500/20 hover:scale-[1.02]"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 shadow-md shadow-pink-500/60 animate-pulse"></div>
                              <span className="text-slate-300 text-xs font-medium leading-tight">{stat.timeSlot}</span>
                            </div>
                            <span className="text-pink-400 font-bold text-base tabular-nums">{stat.orderCount}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 3. 單筆銷售數量分析 - 3D圓形圖 + 數據列表 */}
            {quantityStats.length > 0 && (
              <Card className="bg-card/80 border-border">
                <CardHeader>
                  <CardTitle className="text-foreground text-lg">
                    {language === 'zh' ? '單筆銷售數量分析' : 'Sales Quantity Analysis'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-6 items-center">
                    {/* 左側：3D圓形圖（自動置中）*/}
                    <div className="flex-1 flex items-center justify-center" style={{ minHeight: '550px' }}>
                      <ResponsiveContainer width="100%" height={550}>
                        <PieChart margin={{ top: 40, right: 40, bottom: 40, left: 40 }}>
                          <defs>
                            {/* 3D漸層效果 */}
                            {CHART_COLORS.map((color, idx) => (
                              <radialGradient key={`gradient-${idx}`} id={`pieGradient${idx}`} cx="30%" cy="30%">
                                <stop offset="0%" stopColor={color} stopOpacity={1}/>
                                <stop offset="100%" stopColor={color} stopOpacity={0.7}/>
                              </radialGradient>
                            ))}
                            {/* 陰影效果 */}
                            <filter id="pieShadow" x="-50%" y="-50%" width="200%" height="200%">
                              <feGaussianBlur in="SourceAlpha" stdDeviation="6"/>
                              <feOffset dx="4" dy="8" result="offsetblur"/>
                              <feComponentTransfer>
                                <feFuncA type="linear" slope="0.5"/>
                              </feComponentTransfer>
                              <feMerge>
                                <feMergeNode/>
                                <feMergeNode in="SourceGraphic"/>
                              </feMerge>
                            </filter>
                          </defs>
                          <Pie
                            data={quantityStats}
                            dataKey="count"
                            nameKey="quantity"
                            cx="50%"
                            cy="50%"
                            innerRadius={0}
                            outerRadius={180}
                            paddingAngle={3}
                            filter="url(#pieShadow)"
                            isAnimationActive={true}
                            style={{ zIndex: 10 }}
                          >
                            {quantityStats.map((_, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={`url(#pieGradient${index % CHART_COLORS.length})`}
                                stroke="#1e293b"
                                strokeWidth={3}
                                style={{
                                  filter: 'drop-shadow(3px 6px 8px rgba(0, 0, 0, 0.3))'
                                }}
                              />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#0f172a', 
                              border: '2px solid #8b5cf6', 
                              borderRadius: '12px',
                              boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3)',
                              padding: '12px',
                              zIndex: 1000
                            }}
                            itemStyle={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600 }}
                            formatter={(value, name) => {
                              const total = quantityStats.reduce((sum, item) => sum + item.count, 0);
                              const percentage = ((value as number / total) * 100).toFixed(1);
                              return [`${value} ${language === 'zh' ? '單' : 'orders'} (${percentage}%)`, `${name}${language === 'zh' ? '件' : ' items'}`];
                            }}
                            wrapperStyle={{ zIndex: 1000 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* 右側：數據文字列表 */}
                    <div className="w-48 flex flex-col justify-center space-y-3" style={{ minHeight: '550px' }}>
                      {quantityStats.map((stat, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between py-2 px-3 bg-gradient-to-r from-slate-700/40 to-slate-700/20 rounded-lg border border-slate-600/30 hover:border-purple-500/50 transition-all duration-300"
                        >
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full shadow-md"
                              style={{ 
                                backgroundColor: CHART_COLORS[idx % CHART_COLORS.length],
                                boxShadow: `0 0 8px ${CHART_COLORS[idx % CHART_COLORS.length]}80`
                              }}
                            ></div>
                            <span className="text-slate-300 text-sm font-medium">
                              {stat.quantity}{language === 'zh' ? '件' : ' items'}
                            </span>
                          </div>
                          <span className="text-purple-400 font-bold text-base tabular-nums">
                            {stat.count}{language === 'zh' ? '單' : ''}
                          </span>
                        </div>
                      ))}
                      
                      {/* 總數 */}
                      <div className="flex items-center justify-between py-3 px-3 bg-gradient-to-r from-purple-900/40 to-purple-800/30 rounded-lg border-2 border-purple-500/60 mt-4">
                        <span className="text-purple-200 text-sm font-bold">
                          {language === 'zh' ? '總數' : 'Total'}
                        </span>
                        <span className="text-purple-300 font-bold text-lg tabular-nums">
                          {quantityStats.reduce((sum, stat) => sum + stat.count, 0)}{language === 'zh' ? '單' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 4. 單筆銷售包含不同的產品 - 3D圓形圖 + 數據列表 */}
            {skuCountStats.length > 0 && (
              <Card className="bg-card/80 border-border">
                <CardHeader>
                  <CardTitle className="text-foreground text-lg">
                    {language === 'zh' ? '單筆銷售包含不同的產品' : 'Different Products Per Order'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-6 items-center">
                    {/* 左側：3D圓形圖（自動置中）*/}
                    <div className="flex-1 flex items-center justify-center" style={{ minHeight: '550px' }}>
                      <ResponsiveContainer width="100%" height={550}>
                        <PieChart margin={{ top: 40, right: 40, bottom: 40, left: 40 }}>
                          <defs>
                            {/* 3D漸層效果 */}
                            {CHART_COLORS.map((color, idx) => (
                              <radialGradient key={`gradient-sku-${idx}`} id={`skuPieGradient${idx}`} cx="30%" cy="30%">
                                <stop offset="0%" stopColor={color} stopOpacity={1}/>
                                <stop offset="100%" stopColor={color} stopOpacity={0.7}/>
                              </radialGradient>
                            ))}
                            {/* 陰影效果 */}
                            <filter id="skuPieShadow" x="-50%" y="-50%" width="200%" height="200%">
                              <feGaussianBlur in="SourceAlpha" stdDeviation="6"/>
                              <feOffset dx="4" dy="8" result="offsetblur"/>
                              <feComponentTransfer>
                                <feFuncA type="linear" slope="0.5"/>
                              </feComponentTransfer>
                              <feMerge>
                                <feMergeNode/>
                                <feMergeNode in="SourceGraphic"/>
                              </feMerge>
                            </filter>
                          </defs>
                          <Pie
                            data={skuCountStats}
                            dataKey="count"
                            nameKey="quantity"
                            cx="50%"
                            cy="50%"
                            innerRadius={0}
                            outerRadius={180}
                            paddingAngle={3}
                            filter="url(#skuPieShadow)"
                            isAnimationActive={true}
                            style={{ zIndex: 10 }}
                          >
                            {skuCountStats.map((_, index) => (
                              <Cell 
                                key={`cell-sku-${index}`} 
                                fill={`url(#skuPieGradient${index % CHART_COLORS.length})`}
                                stroke="#1e293b"
                                strokeWidth={3}
                                style={{
                                  filter: 'drop-shadow(3px 6px 8px rgba(0, 0, 0, 0.3))'
                                }}
                              />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#0f172a', 
                              border: '2px solid #06b6d4', 
                              borderRadius: '12px',
                              boxShadow: '0 8px 24px rgba(6, 182, 212, 0.3)',
                              padding: '12px',
                              zIndex: 1000
                            }}
                            itemStyle={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600 }}
                            formatter={(value, name) => {
                              const total = skuCountStats.reduce((sum, item) => sum + item.count, 0);
                              const percentage = ((value as number / total) * 100).toFixed(1);
                              return [`${value} ${language === 'zh' ? '單' : 'orders'} (${percentage}%)`, `${name}${language === 'zh' ? '種' : ' types'}`];
                            }}
                            wrapperStyle={{ zIndex: 1000 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* 右側：數據文字列表 */}
                    <div className="w-48 flex flex-col justify-center space-y-3" style={{ minHeight: '550px' }}>
                      {skuCountStats.map((stat, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between py-2 px-3 bg-gradient-to-r from-slate-700/40 to-slate-700/20 rounded-lg border border-slate-600/30 hover:border-cyan-500/50 transition-all duration-300"
                        >
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full shadow-md"
                              style={{ 
                                backgroundColor: CHART_COLORS[idx % CHART_COLORS.length],
                                boxShadow: `0 0 8px ${CHART_COLORS[idx % CHART_COLORS.length]}80`
                              }}
                            ></div>
                            <span className="text-slate-300 text-sm font-medium">
                              {stat.quantity}{language === 'zh' ? '種' : ' types'}
                            </span>
                          </div>
                          <span className="text-cyan-400 font-bold text-base tabular-nums">
                            {stat.count}{language === 'zh' ? '單' : ''}
                          </span>
                        </div>
                      ))}
                      
                      {/* 總數 */}
                      <div className="flex items-center justify-between py-3 px-3 bg-gradient-to-r from-cyan-900/40 to-cyan-800/30 rounded-lg border-2 border-cyan-500/60 mt-4">
                        <span className="text-cyan-200 text-sm font-bold">
                          {language === 'zh' ? '總數' : 'Total'}
                        </span>
                        <span className="text-cyan-300 font-bold text-lg tabular-nums">
                          {skuCountStats.reduce((sum, stat) => sum + stat.count, 0)}{language === 'zh' ? '單' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 5. 銷售地區分布（前15名）- 3D柱狀圖 */}
            {districtStats.length > 0 && (
              <Card className="bg-card/80 border-border lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-foreground text-lg">
                    {language === 'zh' ? '銷售地區分布（前15名）' : 'Sales District Distribution (Top 15)'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[450px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={districtStats} layout="vertical" barCategoryGap="15%">
                        <defs>
                          {/* 3D漸層效果 - 主色 */}
                          <linearGradient id="colorDistrictBar3D" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.9}/>
                            <stop offset="50%" stopColor="#34d399" stopOpacity={1}/>
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0.8}/>
                          </linearGradient>
                          {/* 側面陰影效果 */}
                          <linearGradient id="colorDistrictBarSide" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#059669" stopOpacity={1}/>
                            <stop offset="100%" stopColor="#047857" stopOpacity={0.9}/>
                          </linearGradient>
                          {/* 3D陰影濾鏡 */}
                          <filter id="districtShadow3D" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur in="SourceAlpha" stdDeviation="4"/>
                            <feOffset dx="3" dy="4" result="offsetblur"/>
                            <feComponentTransfer>
                              <feFuncA type="linear" slope="0.4"/>
                            </feComponentTransfer>
                            <feMerge>
                              <feMergeNode/>
                              <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                          </filter>
                          {/* 頂部光澤效果 */}
                          <filter id="districtTopGloss" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
                            <feOffset dy="-1" result="offsetblur"/>
                            <feFlood floodColor="#d1fae5" floodOpacity="0.3"/>
                            <feComposite in2="offsetblur" operator="in"/>
                            <feMerge>
                              <feMergeNode/>
                              <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                          </filter>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#475569" strokeOpacity={0.3} />
                        <XAxis 
                          type="number" 
                          stroke="#94a3b8" 
                          tick={{ fill: '#cbd5e1', fontSize: 13 }}
                          axisLine={{ stroke: '#475569', strokeWidth: 2 }}
                        />
                        <YAxis 
                          type="category" 
                          dataKey="district" 
                          stroke="#94a3b8"
                          width={140}
                          tick={{ fill: '#cbd5e1', fontSize: 13, fontWeight: 500 }}
                          axisLine={{ stroke: '#475569', strokeWidth: 2 }}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#0f172a', 
                            border: '2px solid #10b981', 
                            borderRadius: '12px',
                            boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)',
                            padding: '12px'
                          }}
                          labelStyle={{ color: '#cbd5e1', fontSize: 14, fontWeight: 600, marginBottom: 8 }}
                          itemStyle={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600 }}
                          cursor={{ fill: 'rgba(16, 185, 129, 0.1)' }}
                        />
                        <Bar 
                          dataKey="orderCount" 
                          fill="url(#colorDistrictBar3D)"
                          name={language === 'zh' ? '訂單數' : 'Orders'}
                          radius={[0, 12, 12, 0]}
                          filter="url(#districtShadow3D)"
                          maxBarSize={35}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}


        {/* 無資料顯示 */}
        {!loading && !hasData && (
          <Card className="bg-card/80 border-border">
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">
                {language === 'zh' ? '請使用上方搜尋條件開始流量分析' : 'Please use the search criteria above to start traffic analysis'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TrafficAnalysis;

